/**
 * Os cinco estados da §11.1, cada um com desenho próprio (H03).
 *
 * ── Por que são componentes, e não `if` em cada tela ──────────────────────
 *
 * A §11.1 chama os cinco de *estados de primeira classe*. Na prática isso quer
 * dizer que eles têm o mesmo peso que o estado "deu certo" — não são o
 * tratamento de exceção que sobra no fim do arquivo.
 *
 * Espalhados por quinze telas, degeneram sempre igual: um spinner central
 * genérico, uma lista vazia sem explicação, um "algo deu errado" sem código
 * para o suporte, e o modo degradado tratado como falha. Aqui eles são
 * componentes, e melhorar um melhora as quinze.
 *
 * ── A armadilha que a task nomeia ─────────────────────────────────────────
 *
 * *"Tratar modo degradado como erro. Com o Twenty fora, a conversa ainda pode
 * ser lida e respondida — bloquear a tela inteira transforma degradação parcial
 * em indisponibilidade total."*
 *
 * Por isso o degradado é uma FAIXA por cima do conteúdo, e nunca substitui o
 * conteúdo. Os outros quatro substituem, porque nos quatro não há conteúdo.
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { useSessao } from '@/shared/sessao/contexto';
import { useEstadoDoFluxo } from '@/shared/api/provedor';
import { diagnosticar, podeForcarAtualizacao } from './diagnostico';
import { idadeEmPalavras, instantePorExtenso } from './idade';
import './estados.css';
import { Botao, Icone } from '@/shared/ui';

// ── 1. Carregando ─────────────────────────────────────────────────────────

export type FormaDoEsqueleto = 'lista' | 'cartoes' | 'ficha' | 'conversa' | 'linha';

/**
 * Esqueleto com a FORMA do conteúdo — não um spinner central.
 *
 * A razão é medível: o esqueleto ocupa o mesmo espaço que o conteúdo vai
 * ocupar, então nada pula de lugar quando os dados chegam. Spinner central
 * some e o conteúdo aparece de tamanho diferente, e o olho recomeça a leitura.
 *
 * A forma é obrigatória de propósito. Um `<Carregando />` sem forma viraria o
 * spinner genérico com outro nome.
 */
export function Carregando({
  forma,
  linhas = 4,
  rotulo = 'Carregando',
}: {
  forma: FormaDoEsqueleto;
  linhas?: number;
  rotulo?: string;
}): ReactNode {
  return (
    <div
      className={`esq esq--${forma}`}
      /*
       * `role="status"` e não um `div` com `aria-label`.
       *
       * O axe reprova nome acessível em elemento sem papel — e com razão: um
       * `div` genérico não é anunciado, então o rótulo se perde. O `status`
       * aceita nome, já implica `aria-live="polite"`, e diz o que a região é.
       *
       * O `aria-busy` fica: ele é o que faz o leitor anunciar uma vez que está
       * carregando, em vez de ler cada osso do esqueleto.
       */
      role="status"
      aria-busy="true"
      aria-label={rotulo}
    >
      {Array.from({ length: linhas }, (_, i) => (
        <div className="esq__item" key={i}>
          {(forma === 'lista' || forma === 'conversa') && <div className="esq__circulo" />}
          <div className="esq__texto">
            <div className="esq__barra" style={{ width: `${58 + ((i * 13) % 30)}%` }} />
            <div
              className="esq__barra esq__barra--fina"
              style={{ width: `${34 + ((i * 17) % 44)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── 2. Vazio ──────────────────────────────────────────────────────────────

/**
 * Vazio que EXPLICA.
 *
 * `porque` é obrigatório, e é o que separa este componente de uma frase
 * genérica. "Nenhuma conversa" não diz se a fila está limpa ou se um filtro
 * escondeu tudo — e a diferença entre as duas é o dia inteiro de quem atende.
 */
export function Vazio({
  titulo,
  porque,
  acao,
}: {
  titulo: string;
  porque: string;
  acao?: { rotulo: string; aoClicar: () => void };
}): ReactNode {
  return (
    <div className="est est--vazio">
      <h3 className="est__titulo">{titulo}</h3>
      <p className="est__texto">{porque}</p>
      {acao && <Botao onClick={acao.aoClicar}>{acao.rotulo}</Botao>}
    </div>
  );
}

// ── 3. Erro ───────────────────────────────────────────────────────────────

function CodigoDeSuporte({ correlationId }: { correlationId: string }): ReactNode {
  const [copiado, definirCopiado] = useState(false);

  return (
    <p className="est__correlacao">
      <span>Se continuar, passe este código ao suporte:</span>
      <code>{correlationId}</code>
      <Botao
        variante="fantasma"
        miudo
        onClick={() => {
          // Copiar é o que evita o erro de transcrição: são 36 caracteres, e
          // um dígito trocado leva o suporte ao log de outra requisição.
          void navigator.clipboard?.writeText(correlationId).then(() => definirCopiado(true));
        }}
      >
        {copiado ? 'copiado' : 'copiar'}
      </Botao>
    </p>
  );
}

/**
 * Erro, com mensagem segura e código de suporte.
 *
 * A mensagem NUNCA é a do erro cru. O `ProblemaDaApi` já traz um título do
 * catálogo (B34), escrito para ser lido por quem usa; o detalhe técnico fica no
 * log do servidor, junto do `correlation_id`. Jogar a exceção na tela vazaria
 * nome de tabela e caminho de arquivo para o atendente.
 *
 * `podeRepetir` sai do catálogo, não de um palpite. Oferecer "tentar de novo"
 * num 400 ensina a clicar num botão que nunca vai funcionar.
 */
export function Erro({
  erro,
  aoRepetir,
  titulo = 'Não foi possível carregar',
}: {
  erro: unknown;
  aoRepetir?: () => void;
  titulo?: string;
}): ReactNode {
  const veredito = diagnosticar(erro);

  if (veredito.tipo === 'sem-acesso') return <SemAcesso />;
  if (veredito.tipo === 'sem-permissao') return <SemPermissao />;

  const podeRepetir = veredito.tipo === 'erro' ? veredito.podeRepetir : true;
  const correlationId = veredito.tipo === 'sem-sessao' ? null : veredito.correlationId;

  return (
    <div className="est est--erro" role="alert">
      <h3 className="est__titulo">{titulo}</h3>
      <p className="est__texto">
        {veredito.tipo === 'degradado'
          ? 'Um sistema de que esta tela depende está fora do ar. Já foi avisado; tente de novo em instantes.'
          : podeRepetir
            ? 'A tentativa falhou. Isso costuma ser passageiro.'
            : 'A tentativa falhou e repetir não deve resolver.'}
      </p>

      {correlationId && <CodigoDeSuporte correlationId={correlationId} />}

      {podeRepetir && aoRepetir && (
        <Botao variante="primario" onClick={aoRepetir}>
          Tentar de novo
        </Botao>
      )}
    </div>
  );
}

// ── 4. Sem acesso ─────────────────────────────────────────────────────────

/**
 * Não encontrado OU sem acesso — e a tela não diz qual.
 *
 * A B16 devolve 404 nos dois casos e a §19 explica: 403 confirmaria que o
 * registro existe, e daria para descobrir a carteira do concorrente testando
 * identificadores. Escrever "sem permissão" aqui desfaria, na tela, a proteção
 * que o servidor tomou o cuidado de construir.
 *
 * O texto é deliberadamente ambíguo, e a ambiguidade é o recurso.
 */
export function SemAcesso(): ReactNode {
  return (
    <div className="est est--neutro" role="status">
      <h3 className="est__titulo">Não encontrado</h3>
      <p className="est__texto">
        Este item não existe ou não está disponível para você. Se esperava encontrá-lo, confira o
        endereço ou fale com o administrador da sua organização.
      </p>
    </div>
  );
}

/**
 * A operação existe e esta pessoa não pode executá-la.
 *
 * Diferente do anterior: aqui o servidor já decidiu revelar, porque o 403 é
 * sobre uma AÇÃO, não sobre um recurso. Dizer "você não pode mudar a política
 * de MFA" não conta nada que o papel da pessoa não deixasse deduzir.
 */
export function SemPermissao(): ReactNode {
  return (
    <div className="est est--neutro" role="status">
      <h3 className="est__titulo">Você não tem acesso a esta área</h3>
      <p className="est__texto">
        Seu papel não inclui esta operação. Fale com o administrador da sua organização se precisar
        dela.
      </p>
    </div>
  );
}

// ── 5. Modo degradado ─────────────────────────────────────────────────────

/**
 * A faixa de dado defasado (§14.3).
 *
 * Fica POR CIMA do conteúdo, nunca no lugar dele. É a diferença entre informar
 * e travar: com o CRM fora, a conversa continua legível e respondível, e
 * bloquear a tela transformaria degradação parcial em parada total.
 *
 * O botão de atualizar só aparece para quem responde pela operação — ver
 * `podeForcarAtualizacao`. Forçar atualização vai à engine que já está ruim, e
 * trinta vendedores clicando somariam trinta chamadas contra quem está caindo.
 */
export function Defasado({
  projetadoEm,
  origem,
  aoAtualizar,
}: {
  projetadoEm: string | null;
  /** O que está defasado, em palavras do usuário: "os dados do CRM". */
  origem?: string;
  aoAtualizar?: () => void;
}): ReactNode {
  const { estado } = useSessao();
  const papel = estado.nome === 'autenticado' ? estado.me.papel : null;
  const fluxo = useEstadoDoFluxo();

  /*
   * Com o tempo real de pé, a faixa NÃO aparece. Ponto.
   *
   * ── A confusão que ela vinha cometendo ─────────────────────────────────
   *
   * O `stale` do servidor compara `projected_at` com um limiar por tipo — 30 s
   * para conversa. Só que `projected_at` diz quando escrevemos pela última
   * vez, e não o quanto estamos atrasados.
   *
   * Numa conversa em que o cliente falou há cinquenta minutos, a projeção tem
   * cinquenta minutos porque NÃO ACONTECEU NADA desde então. A faixa lia isso
   * como defeito e anunciava "esta conversa são de há 50 minutos" — sobre uma
   * conversa perfeitamente atual.
   *
   * A primeira tentativa foi um limiar de quinze minutos. Reduziu o ruído e
   * não resolveu: a lista de ontem e a conversa de cinquenta minutos passavam
   * dele, e as duas continuavam certas.
   *
   * ── Por que o fluxo é a régua certa ────────────────────────────────────
   *
   * Com o canal de tempo real conectado, cada mudança chega em segundos. A
   * idade da projeção deixa de dizer qualquer coisa sobre atraso — ela só diz
   * quando foi a última novidade, que é outra informação e já está na tela,
   * escrita como "última mensagem há tanto".
   *
   * Sem o fluxo, a faixa volta na hora: aí a tela realmente não sabe o que
   * perdeu, e é disso que a §14.3 fala. Ela pede que a tela informe quando o
   * dado PODE estar velho — não que duvide de si mesma o dia inteiro.
   *
   * ── O que se perde, dito por escrito ───────────────────────────────────
   *
   * Se o projetor morrer com o gateway de eventos de pé, o fluxo continua
   * "ao vivo" e a faixa não acende. O sintoma nesse caso é outro e é mais
   * visível: mensagem nova não chega em tela nenhuma.
   */
  /*
   * "Parado" também cala, e não é o mesmo que "ao vivo".
   *
   * O fluxo fica parado quando a tela não assina nada — o funil, por exemplo,
   * não observa recurso nenhum. Isso não é queda: é uma tela que não usa o
   * tempo real. Tratar como queda fazia o funil dizer "são de há 3 dias" toda
   * vez que ninguém movia um card por três dias, que é o caso normal de uma
   * revenda pequena.
   *
   * A faixa fica para o único caso em que ela informa algo: a conexão CAIU e
   * está tentando voltar. Aí a tela realmente não sabe o que perdeu.
   */
  if (fluxo !== 'reconectando') return null;

  const idade = idadeEmPalavras(projetadoEm);
  const exato = instantePorExtenso(projetadoEm);
  const oQue = origem ?? 'Estes dados';

  /*
   * Duas linhas curtas (v3): o que está defasado e desde quando, em cima; a
   * tranquilização, embaixo e mais apagada. O botão fica à direita, fora do
   * texto — antes ele caía no meio da frase e a faixa virava três linhas.
   */
  return (
    <div className="faixa faixa--defasado" role="status">
      <span className="faixa__icone" aria-hidden="true">
        <Icone nome="relogio" />
      </span>
      <span className="faixa__texto">
        <span className="faixa__titulo">
          {oQue}
          {idade ? (
            <>
              {' · '}
              atualizado{' '}
              <time dateTime={projetadoEm ?? undefined} title={exato ?? undefined}>
                {idade}
              </time>
            </>
          ) : (
            // Sem carimbo: dizer isso é pior que uma idade e melhor que fingir
            // frescor, que é o que a §14.3 proíbe.
            <> · sem data de atualização</>
          )}
        </span>
        <span className="faixa__apoio">Você pode continuar trabalhando.</span>
      </span>

      {aoAtualizar && podeForcarAtualizacao(papel) && (
        <Botao variante="secundario" miudo onClick={aoAtualizar}>
          Atualizar agora
        </Botao>
      )}
    </div>
  );
}

/**
 * Ação bloqueada, com o motivo.
 *
 * O que a §14.3 proíbe é aceitar e torcer. Mas recusar sem dizer por quê
 * produz o mesmo desamparo: o botão não funciona e o usuário tenta de novo.
 * `porque` é obrigatório.
 */
export function AcaoBloqueada({
  porque,
  aoTentarDeNovo,
}: {
  porque: string;
  aoTentarDeNovo?: () => void;
}): ReactNode {
  return (
    <div className="faixa faixa--bloqueado" role="alert">
      <span className="faixa__icone" aria-hidden="true">
        ⚠
      </span>
      <span className="faixa__texto">{porque}</span>
      {aoTentarDeNovo && (
        <Botao variante="fantasma" miudo onClick={aoTentarDeNovo}>
          Tentar de novo
        </Botao>
      )}
    </div>
  );
}

// ── O despachante ─────────────────────────────────────────────────────────

export interface ResultadoDeConsulta<T> {
  readonly data: T | undefined;
  readonly error: unknown;
  readonly isPending: boolean;
  readonly refetch: () => unknown;
}

/**
 * Escolhe o estado a partir do resultado da consulta.
 *
 * É o que faz cada tela de domínio (H04+) escrever o caminho feliz e nada mais.
 * Sem isto, cada uma repetiria a mesma escada de `if` — e é copiando escada de
 * `if` que se esquece do caso de permissão.
 *
 * `vazio` é uma função e não um booleano porque só a tela sabe o que "vazio"
 * significa: lista sem itens, board sem coluna, ficha sem bloco.
 */
export function Estado<T>({
  consulta,
  forma,
  linhas,
  vazio,
  children,
}: {
  consulta: ResultadoDeConsulta<T>;
  forma: FormaDoEsqueleto;
  linhas?: number;
  vazio?: { quando: (dados: T) => boolean; titulo: string; porque: string };
  children: (dados: T) => ReactNode;
}): ReactNode {
  if (consulta.isPending) return <Carregando forma={forma} linhas={linhas} />;

  const falhou = consulta.error !== null && consulta.error !== undefined;

  /*
   * Falha SEM nada na tela: aí sim, a tela de erro.
   *
   * Com dados já carregados, uma atualização que falhou — o limite de
   * requisições, a rede que piscou — não apaga a lista que estava certa. Ela
   * continua, com um aviso discreto em cima, e a próxima tentativa (sozinha,
   * ver `provedor.tsx`) a atualiza. Trocar a lista inteira pela tela de erro
   * era o "a tela quebra" de quem só estava trabalhando.
   */
  if (falhou && consulta.data === undefined) {
    return <Erro erro={consulta.error} aoRepetir={() => void consulta.refetch()} />;
  }

  // `undefined` sem erro e sem estar pendente é consulta desabilitada — ainda
  // falta a sessão, por exemplo. Mostrar esqueleto é honesto: está esperando.
  if (consulta.data === undefined) return <Carregando forma={forma} linhas={linhas} />;

  const aviso = falhou ? <AtualizacaoAtrasada aoRepetir={() => void consulta.refetch()} /> : null;

  if (vazio?.quando(consulta.data)) {
    return (
      <>
        {aviso}
        <Vazio titulo={vazio.titulo} porque={vazio.porque} />
      </>
    );
  }

  return (
    <>
      {aviso}
      {children(consulta.data)}
    </>
  );
}

/**
 * O aviso de que a última atualização não veio, com o que está na tela
 * mantido. Discreto de propósito: a pessoa pode continuar trabalhando.
 */
export function AtualizacaoAtrasada({ aoRepetir }: { aoRepetir: () => void }): ReactNode {
  return (
    <div className="faixa faixa--defasado" role="status">
      <span className="faixa__icone" aria-hidden="true">
        ⏱
      </span>
      <span className="faixa__texto">
        Não foi possível atualizar agora. O que está na tela continua valendo, e tento de novo em
        instantes.
      </span>
      <Botao variante="fantasma" miudo onClick={aoRepetir}>
        Atualizar
      </Botao>
    </div>
  );
}
